'use client';
import React, { useState } from 'react';

interface FormData {
  name: string;
  email: string;
  phone: string;
  message: string;
}

interface FormErrors {
  name?: string;
  email?: string;
  message?: string;
}

export default function ContactSection() {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const validate = (): boolean => {
    const newErrors: FormErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    if (!formData.message.trim()) newErrors.message = 'Message is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    // Simulate form submission
    await new Promise((resolve) => setTimeout(resolve, 1200));
    setSubmitting(false);
    setSubmitted(true);
    setFormData({ name: '', email: '', phone: '', message: '' });
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    backgroundColor: '#fff',
    border: '1px solid #ddd',
    borderRadius: '5px',
    boxShadow: 'none',
    color: '#333',
    fontFamily: 'Lato, sans-serif',
    fontSize: '14px',
    fontWeight: 300,
    padding: '10px',
    outline: 'none',
    boxSizing: 'border-box',
    transition: 'border 0.2s',
  };

  const labelStyle: React.CSSProperties = {
    display: 'block',
    fontFamily: 'Lato, sans-serif',
    fontSize: '12px',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    color: '#555',
    marginBottom: '6px',
  };

  const errorStyle: React.CSSProperties = {
    color: '#c0392b',
    fontSize: '11px',
    fontFamily: 'Lato, sans-serif',
    marginTop: '4px',
  };

  return (
    <div
      id="contact"
      style={{
        width: '100%',
        backgroundColor: '#fff',
        borderTop: '4px solid #0e6585',
      }}
    >
      <div style={{ maxWidth: '1140px', margin: '0 auto', padding: '60px 30px' }}>
        <h2
          style={{
            fontFamily: 'Lato, sans-serif',
            fontSize: '36px',
            fontWeight: 900,
            color: '#0e6585',
            textTransform: 'uppercase',
            textAlign: 'center',
            marginBottom: '8px',
            marginTop: 0,
          }}
        >
          Contact Us
        </h2>
        <p
          style={{
            textAlign: 'center',
            fontFamily: 'Lato, sans-serif',
            fontSize: '16px',
            color: '#666',
            marginBottom: '40px',
          }}
        >
          Get in touch with our team for a free quote or service inquiry
        </p>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '40px' }}>
          {/* Contact Form */}
          <div style={{ flex: '1 1 400px' }}>
            {submitted ? (
              <div
                style={{
                  backgroundColor: '#f0f9f0',
                  border: '1px solid #4caf50',
                  borderRadius: '5px',
                  padding: '30px',
                  textAlign: 'center',
                }}
              >
                <i className="fas fa-check-circle" style={{ color: '#4caf50', fontSize: '48px', marginBottom: '16px', display: 'block' }}></i>
                <h3
                  style={{
                    fontFamily: 'Lato, sans-serif',
                    fontSize: '20px',
                    fontWeight: 700,
                    color: '#333',
                    marginBottom: '8px',
                    marginTop: 0,
                  }}
                >
                  Message Sent!
                </h3>
                <p style={{ fontFamily: 'Lato, sans-serif', fontSize: '14px', color: '#555', margin: 0 }}>
                  Thank you for contacting GOTTSCHO PRINTING SYSTEMS. We&apos;ll get back to you within 1 business day.
                </p>
                <button
                  onClick={() => setSubmitted(false)}
                  style={{
                    marginTop: '20px',
                    backgroundColor: '#0e6585',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '12px',
                    padding: '10px 20px',
                    fontSize: '12px',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    cursor: 'pointer',
                    fontFamily: 'Lato, sans-serif',
                  }}
                >
                  Send Another Message
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} noValidate>
                <div style={{ marginBottom: '20px' }}>
                  <label htmlFor="name" style={labelStyle}>Name *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Your full name"
                    style={{
                      ...inputStyle,
                      borderColor: errors.name ? '#c0392b' : '#ddd',
                    }}
                  />
                  {errors.name && <p style={errorStyle}>{errors.name}</p>}
                </div>

                <div style={{ marginBottom: '20px' }}>
                  <label htmlFor="email" style={labelStyle}>Email *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="your@email.com"
                    style={{
                      ...inputStyle,
                      borderColor: errors.email ? '#c0392b' : '#ddd',
                    }}
                  />
                  {errors.email && <p style={errorStyle}>{errors.email}</p>}
                </div>

                <div style={{ marginBottom: '20px' }}>
                  <label htmlFor="phone" style={labelStyle}>Phone (Optional)</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="(555) 000-0000"
                    style={inputStyle}
                  />
                </div>

                <div style={{ marginBottom: '20px' }}>
                  <label htmlFor="message" style={labelStyle}>Message *</label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Describe your service request or inquiry..."
                    rows={5}
                    style={{
                      ...inputStyle,
                      resize: 'vertical',
                      borderColor: errors.message ? '#c0392b' : '#ddd',
                    }}
                  />
                  {errors.message && <p style={errorStyle}>{errors.message}</p>}
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  style={{
                    backgroundColor: submitting ? '#888' : '#0e6585',
                    border: 'none',
                    boxShadow: 'none',
                    color: '#fff',
                    cursor: submitting ? 'not-allowed' : 'pointer',
                    fontFamily: 'Lato, sans-serif',
                    fontWeight: 600,
                    padding: '12px 24px',
                    textTransform: 'uppercase',
                    width: 'auto',
                    margin: '20px 0 0',
                    fontSize: '12px',
                    borderRadius: '12px',
                    letterSpacing: '0.5px',
                    transition: 'background 0.2s',
                  }}
                  className="submit-btn"
                >
                  {submitting ? (
                    <span>
                      <i className="fas fa-spinner fa-spin" style={{ marginRight: '8px' }}></i>
                      Sending...
                    </span>
                  ) : (
                    'Send Message'
                  )}
                </button>
                <p style={{ fontFamily: 'Lato, sans-serif', fontSize: '11px', color: '#999', marginTop: '10px' }}>
                  * This form sends your inquiry to gottschprisupport@gottschpri.com
                </p>
              </form>
            )}
          </div>

          {/* Contact Info */}
          <div style={{ flex: '1 1 280px' }}>
            <div
              style={{
                backgroundColor: '#f7f7f7',
                borderRadius: '3px',
                padding: '30px',
                borderLeft: '4px solid #0e6585',
              }}
            >
              <h3
                style={{
                  fontFamily: '"Pragati Narrow", sans-serif',
                  fontSize: '20px',
                  fontWeight: 700,
                  color: '#333',
                  textTransform: 'uppercase',
                  marginBottom: '20px',
                  marginTop: 0,
                }}
              >
                Contact Information
              </h3>

              {[
                {
                  icon: 'fa-map-marker-alt',
                  label: 'Address',
                  value: '601 Davisville Rd\nWillow Grove, PA 19090',
                  href: null,
                },
                {
                  icon: 'fa-phone',
                  label: 'Phone',
                  value: '(321) 663-4402',
                  href: 'tel:3216634402',
                },
                {
                  icon: 'fa-envelope',
                  label: 'Email',
                  value: 'gottschprisupport@gottschpri.com',
                  href: 'mailto:gottschprisupport@gottschpri.com',
                },
                {
                  icon: 'fa-clock',
                  label: 'Business Hours',
                  value: 'Mon–Fri: 8:00 AM – 5:00 PM',
                  href: null,
                },
              ].map((item) => (
                <div
                  key={item.label}
                  style={{
                    display: 'flex',
                    gap: '14px',
                    alignItems: 'flex-start',
                    marginBottom: '20px',
                  }}
                >
                  <div
                    style={{
                      width: '36px',
                      height: '36px',
                      backgroundColor: '#0e6585',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <i className={`fas ${item.icon}`} style={{ color: '#fff', fontSize: '14px' }}></i>
                  </div>
                  <div>
                    <div
                      style={{
                        fontFamily: 'Lato, sans-serif',
                        fontSize: '11px',
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        color: '#999',
                        marginBottom: '2px',
                      }}
                    >
                      {item.label}
                    </div>
                    {item.href ? (
                      <a
                        href={item.href}
                        style={{
                          fontFamily: 'Lato, sans-serif',
                          fontSize: '14px',
                          color: '#0e6585',
                          textDecoration: 'none',
                          fontWeight: 400,
                        }}
                        className="contact-link"
                      >
                        {item.value}
                      </a>
                    ) : (
                      <div
                        style={{
                          fontFamily: 'Lato, sans-serif',
                          fontSize: '14px',
                          color: '#444',
                          whiteSpace: 'pre-line',
                        }}
                      >
                        {item.value}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      <style jsx>{`
        .submit-btn:hover:not(:disabled) { background-color: #333 !important; }
        .contact-link:hover { color: #f89921 !important; }
        input:focus, textarea:focus { border-color: #0e6585 !important; outline: none; }
      `}</style>
    </div>
  );
}
